import { figureService } from './figureService';
import { specStore } from '../stores/editorsStore';

/**
 * Service for tracking and synchronizing figure order between Patdraft and Patdraw
 * Figure order is determined by the order figures are referenced in the specification
 */
export class FigureOrderService {
    constructor() {
        this._lastKnownOrder = [];
        this._isSubscribed = false;
    }

    /**
     * Start monitoring the spec editor for figure reference changes
     */
    startMonitoring() {
        if (this._isSubscribed) return;
        
        this._unsubscribe = specStore.subscribe(state => {
            if (state.content) {
                this.checkAndUpdateFigureOrder(state.content);
            }
        });
        
        this._isSubscribed = true;
        console.log('Figure order monitoring started');
    }

    /**
     * Stop monitoring the spec editor
     */
    stopMonitoring() {
        if (this._unsubscribe) {
            this._unsubscribe();
            this._isSubscribed = false;
            console.log('Figure order monitoring stopped');
        }
    }

    /**
     * Extract figure references from spec content and their order
     * NOTE: Only figure references are extracted, NOT numeral references
     * Numeral references don't affect figure ordering
     * @param {string} specHtml - HTML content from the spec editor
     * @returns {Array<string>} Array of figure IDs in order of appearance
     */
    async extractFigureOrder(specHtml) {
        console.log('🔎 Starting figure order extraction, HTML length:', specHtml?.length || 0);
        console.log('🔎 First 500 chars of HTML:', specHtml?.substring(0, 500));
        
        const parser = new DOMParser();
        const doc = parser.parseFromString(specHtml, 'text/html');
        const orderedFigures = [];
        
        // Check if there are any elements with data-figure-key
        const allFigureRefs = doc.querySelectorAll('[data-figure-key]');
        console.log(`🔎 Found ${allFigureRefs.length} elements with data-figure-key attribute`);
        allFigureRefs.forEach((el, idx) => {
            console.log(`  [${idx + 1}] key="${el.getAttribute('data-figure-key')}", text="${el.textContent}"`);
        });
        
        // Get numerals to filter them out - we only want actual figures for ordering
        let numeralNames = new Set();
        try {
            const numerals = await figureService.fetchNumerals();
            numeralNames = new Set(numerals.map(n => n.name).filter(n => n));
        } catch (error) {
            console.warn('Could not fetch numerals for filtering, proceeding with all references:', error);
        }
        
        // Walk through all elements to find figure references in order
        const walker = document.createTreeWalker(
            doc.body,
            NodeFilter.SHOW_ELEMENT,
            {
                acceptNode: (node) => {
                    return node.hasAttribute('data-figure-key') ? 
                           NodeFilter.FILTER_ACCEPT : 
                           NodeFilter.FILTER_SKIP;
                }
            }
        );

        let node;
        let debugIndex = 0;
        while ((node = walker.nextNode())) {
            const figureKey = node.getAttribute('data-figure-key');
            const nodeText = node.textContent;
            debugIndex++;
            
            console.log(`🔍 [${debugIndex}] Checking node: text="${nodeText}", key="${figureKey}"`);
            
            // Skip if this is a numeral reference (numerals don't affect figure ordering)
            if (figureKey && !numeralNames.has(figureKey)) {
                if (!orderedFigures.includes(figureKey)) {
                    orderedFigures.push(figureKey);
                    console.log(`✅ Added to order at position ${orderedFigures.length}: "${figureKey}"`);
                } else {
                    console.log(`⏭️ Already in order, skipping: "${figureKey}"`);
                }
            }
        }

        console.log('📋 Final extracted figure order:', orderedFigures);
        return orderedFigures;
    }

    /**
     * Check if figure order has changed and update Patdraw if needed
     * @param {string} specHtml - HTML content from the spec editor
     */
    async checkAndUpdateFigureOrder(specHtml) {
        const currentOrder = await this.extractFigureOrder(specHtml);
        
        // Check if order has changed
        if (this.hasOrderChanged(currentOrder, this._lastKnownOrder)) {
            console.log('Figure order changed:', {
                old: this._lastKnownOrder,
                new: currentOrder
            });
            
            // The currentOrder now contains figure IDs directly (not names)
            // Build the figure numbers mapping
            const orderedFigureIds = [...currentOrder];
            const figureNumbers = {};
            
            currentOrder.forEach((figureId, index) => {
                figureNumbers[figureId] = index + 1;
            });
            
            // Only update if we have valid figure IDs
            if (orderedFigureIds.length > 0) {
                // Update Patdraw with the new order using figure IDs
                try {
                    await figureService.updateFigureOrder(orderedFigureIds, figureNumbers);
                    this._lastKnownOrder = [...currentOrder];
                    console.log('Successfully updated figure order in Patdraw');
                    
                    // Also update our backend so Patdraw can fetch it later
                    try {
                        const isProduction = window.location.hostname === 'patcraft-psi.vercel.app' || 
                                           window.location.hostname === 'patcraft-demo.fearn.ai' ||
                                           window.location.hostname.includes('vercel.app');
                        const baseUrl = import.meta.env?.VITE_API_URL || 
                                      (isProduction ? 'https://patcraft-production.up.railway.app' : 'http://localhost:5001');
                        const response = await fetch(`${baseUrl}/update-figure-order`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({
                                orderedFigureIds: orderedFigureIds,  // Now these are already IDs
                                figureNumbers: figureNumbers
                            })
                        });
                        if (!response.ok) {
                            console.error('Failed to update backend figure order:', response.status);
                        }
                    } catch (error) {
                        console.error('Failed to update backend figure order:', error);
                    }
                } catch (error) {
                    console.error('Failed to update figure order in Patdraw:', error);
                }
            }
        }
    }

    /**
     * Check if the figure order has changed
     * @param {Array<string>} newOrder 
     * @param {Array<string>} oldOrder 
     * @returns {boolean}
     */
    hasOrderChanged(newOrder, oldOrder) {
        if (newOrder.length !== oldOrder.length) return true;
        
        for (let i = 0; i < newOrder.length; i++) {
            if (newOrder[i] !== oldOrder[i]) return true;
        }
        
        return false;
    }

    /**
     * Get the current figure order from the spec
     * @returns {Promise<Array<string>>} Array of figure IDs in order
     */
    async getCurrentOrder() {
        let specHtml = '';
        const unsubscribe = specStore.subscribe(state => {
            specHtml = state.content || '';
        });
        unsubscribe();
        
        return await this.extractFigureOrder(specHtml);
    }

    /**
     * Get figure number for a specific figure based on current spec order
     * @param {string} figureId 
     * @returns {Promise<number>} Figure number (1-based) or 0 if not found
     */
    async getFigureNumber(figureId) {
        const currentOrder = await this.getCurrentOrder();
        const index = currentOrder.indexOf(figureId);
        return index >= 0 ? index + 1 : 0;
    }

    /**
     * Get all figure numbers as a mapping
     * @returns {Promise<Object>} Mapping of figure IDs to their numbers
     */
    async getAllFigureNumbers() {
        const currentOrder = await this.getCurrentOrder();
        const figureNumbers = {};
        
        currentOrder.forEach((figureId, index) => {
            figureNumbers[figureId] = index + 1;
        });
        
        return figureNumbers;
    }
}

// Create and export singleton instance
export const figureOrderService = new FigureOrderService();

// Make it available globally for the backend endpoint
if (typeof window !== 'undefined') {
    window.__figureOrderService = figureOrderService;
}